import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { publicApi } from '@services/publicApi';
import { usePublicStore } from '@store/publicStore';

type CallbackStatus = 'loading' | 'success' | 'failed' | 'error';

export default function CheckoutCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = usePublicStore();
  const [status, setStatus] = useState<CallbackStatus>('loading');
  const [orderRef, setOrderRef] = useState<string>('');

  const ref = searchParams.get('order_ref') || '';
  const tapId = searchParams.get('tap_id') || '';

  useEffect(() => {
    if (!ref || !tapId) {
      setStatus('error');
      return;
    }

    setOrderRef(ref);

    const verifyPayment = async () => {
      try {
        const res = await publicApi.getPaymentCallback(ref, tapId);
        const paymentStatus = res.data.status;

        if (paymentStatus === 'paid' || paymentStatus === 'captured') {
          setStatus('success');
          clearCart();
          // Auto-navigate to confirmation after short delay
          setTimeout(() => {
            navigate(`/order/${ref}`, { replace: true });
          }, 2000);
        } else {
          setStatus('failed');
        }
      } catch {
        setStatus('error');
      }
    };

    verifyPayment();
  }, [ref, tapId]);

  return (
    <div className="max-w-lg mx-auto px-4 py-20 text-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        {status === 'loading' && (
          <div>
            <Loader2 className="w-16 h-16 mx-auto text-primary-500 animate-spin mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Verifying Your Payment
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              Please wait while we confirm your payment...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div>
            <div className="w-20 h-20 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Payment Successful!
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Redirecting to your order confirmation...
            </p>
            <Link
              to={`/order/${orderRef}`}
              className="text-primary-600 hover:text-primary-700 font-medium text-sm"
            >
              Click here if not redirected
            </Link>
          </div>
        )}

        {status === 'failed' && (
          <div>
            <div className="w-20 h-20 mx-auto rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Payment Failed
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-8">
              Your payment could not be processed. No charges were made.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/checkout"
                className="px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
              >
                Try Again
              </Link>
              <Link
                to="/events"
                className="px-6 py-3 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Browse Events
              </Link>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div>
            <div className="w-20 h-20 mx-auto rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mb-6">
              <AlertTriangle className="w-10 h-10 text-yellow-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Something Went Wrong
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-8">
              We couldn't verify your payment status. If you were charged, your tickets will be sent via email.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/events"
                className="px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
              >
                Back to Events
              </Link>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
